// The report of a subset of a multi-KPI model's outputs, shaped like the report's root (its
// Total), so the Model Report renders it as it renders the Total. ngtorch builds the Total by
// aggregating the outputs' columns of one model run (ngtorch/app/appreport.py, model_report);
// this rebuilds the same aggregation, for the chosen outputs only, from what the report JSON
// carries per output (report.outputs.<kpi>). Field by field:
// - Sums (exact up to the server's rounding): total, components, marketing, segments, every sales
//   decomposition and media attribution, seasonality, model fit Real / Prediction / confidence
//   bounds (summed as the server does), saturation curves and datapoints (the spend grid of a
//   medium is shared by the outputs), and the ROAS by media (the spend of a medium is shared, so
//   the ROAS of a sum is the sum of the ROAS).
// - Shared, not summed: media spend and inputs over time (union of the outputs' keys); the media
//   mix is recomputed from that spend; invest, dates and budget propagation come from the root.
// - Decay: the mean over the outputs that have the medium, as the server does.
// - Approximate (the server uses data the JSON doesn't carry): the saturation curves' optimal
//   zones, the model quality metrics and the average total ROAS by period.
// - Pricing / distribution effects: summed only when every output plots the same x, else left out.

function isNumber (value) {
  return typeof value === 'number' && isFinite(value)
}

function isSeries (node) {
  return !!node && typeof node === 'object' && Array.isArray(node.x) && Array.isArray(node.y)
}

function isPlainObject (node) {
  return !!node && typeof node === 'object' && !Array.isArray(node)
}

function defined (nodes) {
  return nodes.filter(node => node !== undefined && node !== null)
}

function unionKeys (nodes) {
  const keys = []
  nodes.forEach(node => {
    Object.keys(node).forEach(key => {
      if (keys.indexOf(key) === -1) keys.push(key)
    })
  })
  return keys
}

// x values of several series in one ascending axis. Dates are ISO strings, which sort as text.
function unionAxis (seriesList) {
  const seen = {}
  const axis = []
  seriesList.forEach(series => {
    series.x.forEach(x => {
      if (!(x in seen)) {
        seen[x] = true
        axis.push(x)
      }
    })
  })
  const numeric = axis.every(x => typeof x === 'number')
  return axis.sort((a, b) => numeric ? a - b : (a < b ? -1 : a > b ? 1 : 0))
}

// Combines series point by point over their union of x; `reduce` gets the values at one x (a
// series without that x is left out of them).
function combineSeries (seriesList, reduce) {
  if (seriesList.length === 1) {
    return { x: seriesList[0].x.slice(), y: seriesList[0].y.slice() }
  }
  const axis = unionAxis(seriesList)
  const indexes = seriesList.map(series => {
    const index = {}
    series.x.forEach((x, i) => { index[x] = i })
    return index
  })
  return {
    x: axis,
    y: axis.map(x => {
      const values = []
      seriesList.forEach((series, s) => {
        if (x in indexes[s]) {
          const value = series.y[indexes[s][x]]
          if (isNumber(value)) values.push(value)
        }
      })
      return reduce(values)
    })
  }
}

function sum (values) {
  return values.reduce((total, value) => total + value, 0)
}

function max (values) {
  return values.length > 0 ? Math.max.apply(null, values) : 0
}

// [{ name, value }] lists added up by name, in the order the names first appear.
function sumNamed (lists) {
  const order = []
  const totals = {}
  lists.forEach(list => {
    list.forEach(item => {
      if (!item || typeof item !== 'object') return
      if (!(item.name in totals)) {
        order.push(item.name)
        totals[item.name] = 0
      }
      if (isNumber(item.value)) totals[item.name] += item.value
    })
  })
  return order.map(name => ({ name: name, value: totals[name] }))
}

// Adds up nodes of the same shape: numbers, { x, y } series (by x), [{ name, value }] lists (by
// name) and objects of those (by key). A key missing from some node counts as 0 there. Any other
// value (a unit name, a flag) is the first node's.
function sumTree (nodes) {
  const present = defined(nodes)
  if (present.length === 0) return undefined
  if (present.every(isNumber)) return sum(present)
  if (isSeries(present[0])) return combineSeries(present.filter(isSeries), sum)
  if (Array.isArray(present[0])) return sumNamed(present.filter(node => Array.isArray(node)))
  if (isPlainObject(present[0])) {
    const objects = present.filter(isPlainObject)
    const out = {}
    unionKeys(objects).forEach(key => {
      const value = sumTree(objects.map(node => node[key]))
      if (value !== undefined) out[key] = value
    })
    return out
  }
  return present[0]
}

// Values shared by the outputs (each output carries those of its own media or inputs): the union
// of their keys, each value taken from the first output that has it.
function unionTree (nodes) {
  const present = defined(nodes)
  if (present.length === 0) return undefined
  if (!isPlainObject(present[0]) || isSeries(present[0])) return present[0]
  const objects = present.filter(isPlainObject)
  const out = {}
  unionKeys(objects).forEach(key => {
    const value = unionTree(objects.map(node => node[key]))
    if (value !== undefined) out[key] = value
  })
  return out
}

// A medium's curve object { x, y, ... } as opposed to a group of them (the saturation and decay
// objects are { component: { medium: curve } }, or { medium: curve } in older reports).
function isCurve (node) {
  return isSeries(node)
}

function mapCurves (nodes, combine) {
  const present = defined(nodes).filter(isPlainObject)
  if (present.length === 0) return undefined
  if (isCurve(present[0])) return combine(present.filter(isCurve))
  const out = {}
  unionKeys(present).forEach(key => {
    const value = mapCurves(present.map(node => node[key]), combine)
    if (value !== undefined) out[key] = value
  })
  return out
}

// y of `curve` on the x grid `grid`, linearly interpolated (the outputs share a medium's grid, so
// this is only a guard).
function onGrid (curve, grid) {
  const sameGrid = curve.x.length === grid.length && curve.x.every((x, i) => x === grid[i])
  if (sameGrid) return curve.y
  return grid.map(x => {
    const xs = curve.x
    if (xs.length === 0) return 0
    if (x <= xs[0]) return curve.y[0]
    if (x >= xs[xs.length - 1]) return curve.y[xs.length - 1]
    let i = 1
    while (xs[i] < x) i++
    const t = (x - xs[i - 1]) / (xs[i] - xs[i - 1])
    return curve.y[i - 1] + t * (curve.y[i] - curve.y[i - 1])
  })
}

// The four optimal-zone boundaries of a saturation curve: the spend where the curve is nearest
// 10% of its maximum (threshold) and 90% (diminishing returns), and the spend of the steepest
// segment (max marginal) and of the highest y / x (max ROAS). Approximate: the server takes the
// last two from the median over the model's sampled curves, which the report doesn't carry.
export function approxRegions (x, y) {
  if (x.length < 2) return [0, 0, 0, 0]
  const top = max(y)
  const nearest = target => {
    let best = 0
    for (let i = 1; i < y.length; i++) {
      if (Math.abs(y[i] - target) < Math.abs(y[best] - target)) best = i
    }
    return x[best]
  }
  let maxMarginal = 0
  let bestSlope = -Infinity
  for (let i = 0; i < x.length - 1; i++) {
    const dx = x[i + 1] - x[i]
    if (dx <= 0) continue
    const slope = (y[i + 1] - y[i]) / dx
    if (slope > bestSlope) {
      bestSlope = slope
      maxMarginal = x[i]
    }
  }
  let maxRoas = 0
  let bestRoas = -Infinity
  for (let i = 0; i < x.length; i++) {
    const roas = x[i] > 0 ? y[i] / x[i] : 0
    if (roas > bestRoas) {
      bestRoas = roas
      maxRoas = x[i]
    }
  }
  let threshold = nearest(top * 0.1)
  // As the server: a threshold past the max marginal loses its meaning, so it becomes half of it.
  if (threshold >= maxMarginal) threshold = maxMarginal / 2
  return [threshold, maxMarginal, maxRoas, nearest(top * 0.9)]
}

function sumSaturationCurves (curves) {
  const grid = curves[0].x
  const y = grid.map(() => 0)
  curves.forEach(curve => {
    onGrid(curve, grid).forEach((value, i) => { y[i] += isNumber(value) ? value : 0 })
  })
  const out = { x: grid.slice(), y: y }
  // Datapoints: the spend of a date is shared, the sales add up.
  const withPoints = curves.filter(curve => Array.isArray(curve.datapoints))
  if (withPoints.length > 0) {
    const order = []
    const points = {}
    withPoints.forEach(curve => {
      const dates = Array.isArray(curve.datapoints_dates) ? curve.datapoints_dates : null
      curve.datapoints.forEach((point, i) => {
        const key = dates ? dates[i] : i
        if (!(key in points)) {
          order.push(key)
          points[key] = { x: point.x, y: 0 }
        }
        if (isNumber(point.y)) points[key].y += point.y
      })
    })
    out.datapoints = order.map(key => points[key])
    if (withPoints.some(curve => Array.isArray(curve.datapoints_dates))) {
      out.datapoints_dates = order
    }
  }
  if (curves.some(curve => Array.isArray(curve.regions))) {
    out.regions = approxRegions(out.x, out.y)
  }
  return out
}

function meanDecayCurves (curves) {
  return combineSeries(curves, values => values.length > 0 ? sum(values) / values.length : 0)
}

// Model fit: the series add up; the outlier and epoch flags are 1 where any output has one.
function combineModelFit (fits) {
  const present = defined(fits).filter(isPlainObject)
  if (present.length === 0) return undefined
  const out = {}
  unionKeys(present).forEach(key => {
    const series = present.map(fit => fit[key]).filter(isSeries)
    if (series.length === 0) return
    out[key] = combineSeries(series, key === 'Outliers' || key === 'Epochs' ? max : sum)
  })
  return out
}

// Spearman's rank correlation: Pearson's on the ranks, ties sharing their average rank.
function ranks (values) {
  const order = values.map((value, i) => i).sort((a, b) => values[a] - values[b])
  const out = new Array(values.length)
  let i = 0
  while (i < order.length) {
    let j = i
    while (j + 1 < order.length && values[order[j + 1]] === values[order[i]]) j++
    const rank = (i + j) / 2 + 1
    for (let k = i; k <= j; k++) out[order[k]] = rank
    i = j + 1
  }
  return out
}

function pearson (a, b) {
  const n = a.length
  const meanA = sum(a) / n
  const meanB = sum(b) / n
  let cov = 0
  let varA = 0
  let varB = 0
  for (let i = 0; i < n; i++) {
    cov += (a[i] - meanA) * (b[i] - meanB)
    varA += (a[i] - meanA) * (a[i] - meanA)
    varB += (b[i] - meanB) * (b[i] - meanB)
  }
  return varA > 0 && varB > 0 ? cov / Math.sqrt(varA * varB) : 0
}

// MAPE, RAE, Correlation and AUC with the server's formulas (appreport.py, quality_indicators),
// on the summed model fit: Real stands for the KPI and Real - Prediction for the residual. An
// approximation, since the server uses the unrounded series.
export function approxModelQuality (modelFit) {
  if (!modelFit || !isSeries(modelFit.Real) || !isSeries(modelFit.Prediction)) return undefined
  const predictionAt = {}
  modelFit.Prediction.x.forEach((x, i) => { predictionAt[x] = modelFit.Prediction.y[i] })
  const real = []
  const prediction = []
  modelFit.Real.x.forEach((x, i) => {
    if (x in predictionAt && isNumber(modelFit.Real.y[i]) && isNumber(predictionAt[x])) {
      real.push(modelFit.Real.y[i])
      prediction.push(predictionAt[x])
    }
  })
  if (real.length === 0) return undefined
  const residual = real.map((value, i) => value - prediction[i])

  const nonZero = real.map((value, i) => i).filter(i => real[i] !== 0)
  const mape = nonZero.length > 0
    ? sum(nonZero.map(i => Math.abs(residual[i] / real[i]))) / nonZero.length * 100
    : NaN

  const realAbs = sum(real.map(Math.abs))
  const rae = realAbs > 0 ? sum(residual.map(Math.abs)) / realAbs * 100 : NaN

  const constant = values => values.every(value => value === values[0])
  const correlation = constant(real) || constant(prediction) ? 0 : pearson(ranks(prediction), ranks(real)) * 100

  // Share of periods whose relative error is within each of 1001 tolerances from 0 to 1, integrated.
  const relativeErrors = real.map((value, i) => Math.abs(residual[i]) / value)
  let auc = 0
  for (let k = 0; k <= 1000; k++) {
    const tolerance = k / 1000
    auc += relativeErrors.filter(error => error <= tolerance).length / relativeErrors.length
  }
  auc = auc / 1000 * 100

  return { MAPE: mape, RAE: rae, Correlation: correlation, AUC: auc }
}

// The period of a date at a frequency, as a key that doesn't depend on whether the period is
// labelled by its first or its last day.
function periodKey (date, freq) {
  const text = String(date)
  const year = text.slice(0, 4)
  const month = parseInt(text.slice(5, 7), 10)
  if (freq === 'yearly') return year
  if (freq === 'quarterly') return year + '-Q' + Math.ceil(month / 3)
  return text.slice(0, 7)
}

function dayNumber (date) {
  return Math.round(Date.parse(String(date).slice(0, 10) + 'T00:00:00Z') / 86400000)
}

// Total spend of a { medium: { x, y } } map per period of `labels` (the x of a ROAS series at
// `freq`): monthly, quarterly and yearly by calendar period, weekly by the label within 6 days.
function spendByLabel (spendMap, labels, freq) {
  const totals = labels.map(() => 0)
  if (!spendMap) return totals
  const index = {}
  if (freq === 'weekly') {
    const days = labels.map(dayNumber)
    Object.keys(spendMap).forEach(medium => {
      const series = spendMap[medium]
      if (!isSeries(series)) return
      series.x.forEach((x, i) => {
        const day = dayNumber(x)
        let best = -1
        days.forEach((labelDay, l) => {
          if (Math.abs(labelDay - day) <= 6 && (best === -1 || Math.abs(labelDay - day) < Math.abs(days[best] - day))) best = l
        })
        if (best !== -1 && isNumber(series.y[i])) totals[best] += series.y[i]
      })
    })
    return totals
  }
  labels.forEach((label, l) => { index[periodKey(label, freq)] = l })
  Object.keys(spendMap).forEach(medium => {
    const series = spendMap[medium]
    if (!isSeries(series)) return
    series.x.forEach((x, i) => {
      const l = index[periodKey(x, freq)]
      if (l !== undefined && isNumber(series.y[i])) totals[l] += series.y[i]
    })
  })
  return totals
}

// The average total ROAS by period of the subset: the attributed sales of each output (its total
// ROAS times the spend of its media) added up, over the spend of all the subset's media. The
// server divides the unrounded sales by the spend; this works from the rounded ROAS and spend,
// so it is approximate.
function combineRoasByFreq (v2s, unionSpend, unionSpendWeekly) {
  const present = v2s.filter(v2 => v2 && isPlainObject(v2.roas_average_total_by_freq))
  if (present.length === 0) return undefined
  const freqs = unionKeys(present.map(v2 => v2.roas_average_total_by_freq))
  const out = {}
  freqs.forEach(freq => {
    const series = present.map(v2 => v2.roas_average_total_by_freq[freq]).filter(isSeries)
    if (series.length === 0) return
    const labels = unionAxis(series)
    const spendOf = map => spendByLabel(map, labels, freq)
    const pickSpend = v2 => freq === 'weekly' ? v2.media_spend_weekly : v2.media_spend
    const sales = labels.map(() => 0)
    present.forEach(v2 => {
      const roas = v2.roas_average_total_by_freq[freq]
      if (!isSeries(roas)) return
      const spend = spendOf(pickSpend(v2))
      const at = {}
      roas.x.forEach((x, i) => { at[x] = roas.y[i] })
      labels.forEach((label, l) => {
        if (isNumber(at[label])) sales[l] += at[label] * spend[l]
      })
    })
    const totalSpend = spendOf(freq === 'weekly' ? unionSpendWeekly : unionSpend)
    out[freq] = {
      x: labels,
      y: labels.map((label, l) => totalSpend[l] > 0 ? sales[l] / totalSpend[l] : 0)
    }
  })
  return out
}

// Each medium's share of the period's spend, in whole percents adding up to 100 per period like
// the server's (largest remainders round up).
function mediaMixFromSpend (spendMap) {
  if (!spendMap) return undefined
  const media = Object.keys(spendMap).filter(medium => isSeries(spendMap[medium]))
  if (media.length === 0) return undefined
  const axis = unionAxis(media.map(medium => spendMap[medium]))
  const values = media.map(medium => {
    const at = {}
    spendMap[medium].x.forEach((x, i) => { at[x] = spendMap[medium].y[i] })
    return axis.map(x => isNumber(at[x]) ? at[x] : 0)
  })
  const shares = media.map(() => axis.map(() => 0))
  axis.forEach((x, t) => {
    const total = sum(values.map(row => row[t]))
    if (total <= 0) return
    const raw = values.map(row => row[t] / total * 100)
    const floors = raw.map(Math.floor)
    let left = 100 - sum(floors)
    raw.map((value, m) => m)
      .sort((a, b) => (raw[b] - floors[b]) - (raw[a] - floors[a]))
      .forEach(m => {
        if (left > 0) {
          floors[m]++
          left--
        }
      })
    floors.forEach((value, m) => { shares[m][t] = value })
  })
  const out = {}
  media.forEach((medium, m) => { out[medium] = { x: axis.slice(), y: shares[m] } })
  return out
}

// Pricing / distribution scatter: the server plots the first price or distribution series against
// the summed driver, so the outputs only add up when they all plot the same x.
function combineEffects (effectsList) {
  const present = defined(effectsList).filter(isPlainObject)
  if (present.length === 0) return undefined
  const out = {}
  unionKeys(present).forEach(key => {
    const plots = present.map(effects => effects[key]).filter(isSeries)
    if (plots.length === 0) return
    const x = plots[0].x
    const sameX = plots.every(plot => plot.x.length === x.length && plot.x.every((value, i) => value === x[i]))
    if (!sameX) return
    out[key] = {
      ...plots[0],
      x: x.slice(),
      y: x.map((value, i) => sum(plots.map(plot => plot.y[i]).filter(isNumber)))
    }
  })
  return Object.keys(out).length > 0 ? out : undefined
}

function setIfDefined (target, key, value) {
  if (value !== undefined) target[key] = value
}

// The report of `names` (keys of report.outputs) added up, shaped like the report's root. Works
// on the raw report: pretty names are applied afterwards, as for any output.
export function combineOutputs (report, names) {
  if (!report || !report.outputs) return null
  const outputs = names.map(name => report.outputs[name]).filter(isPlainObject)
  if (outputs.length === 0) return null
  const pick = key => outputs.map(output => output[key])
  const v2s = outputs.map(output => isPlainObject(output.v2) ? output.v2 : null).filter(Boolean)
  const pickV2 = key => v2s.map(v2 => v2[key])

  const combined = {}
  // Shared by every output, as in the Total.
  ;['metadata', 'start_date', 'end_date', 'invest', 'marketing_scales', 'microaudience'].forEach(key => {
    if (key in report) combined[key] = report[key]
  })
  setIfDefined(combined, 'total', sumTree(pick('total')))
  setIfDefined(combined, 'components', sumTree(pick('components')))
  setIfDefined(combined, 'marketing', sumTree(pick('marketing')))
  setIfDefined(combined, 'segments', sumTree(pick('segments')))
  setIfDefined(combined, 'saturation', mapCurves(pick('saturation'), sumSaturationCurves))
  setIfDefined(combined, 'decay', mapCurves(pick('decay'), meanDecayCurves))

  if (v2s.length > 0) {
    const v2 = {}
    ;[
      'sales_decomposition_by_period',
      'sales_decomposition_by_period_weekly',
      'sales_decomposition_total',
      'media_attributed_sales_decomposition_by_period',
      'media_attributed_sales_decomposition_by_period_weekly',
      'media_attributed_sales_decomposition_total',
      'seasonality',
      'seasonality_weekly',
      'total_roas_by_media',
      'roas_average_media_by_freq'
    ].forEach(key => setIfDefined(v2, key, sumTree(pickV2(key))))
    setIfDefined(v2, 'model_fit', combineModelFit(pickV2('model_fit')))
    setIfDefined(v2, 'model_fit_weekly', combineModelFit(pickV2('model_fit_weekly')))
    ;['media_spend', 'media_spend_weekly', 'inputs_over_time'].forEach(key => setIfDefined(v2, key, unionTree(pickV2(key))))
    setIfDefined(v2, 'media_mix', mediaMixFromSpend(v2.media_spend))
    setIfDefined(v2, 'media_mix_weekly', mediaMixFromSpend(v2.media_spend_weekly))
    setIfDefined(v2, 'roas_average_total_by_freq', combineRoasByFreq(v2s, v2.media_spend, v2.media_spend_weekly))
    setIfDefined(v2, 'effects', combineEffects(pickV2('effects')))
    // The finest model fit is the one the server's metrics are computed on.
    const quality = approxModelQuality(v2.model_fit_weekly || v2.model_fit)
    if (quality && v2s.some(output => output.model_quality)) v2.model_quality = quality
    const rootV2 = report.v2 || {}
    ;['roi_unit_name', 'budget_propagation'].forEach(key => {
      if (key in rootV2) v2[key] = rootV2[key]
    })
    combined.v2 = v2
  }
  return combined
}
